import { create, Mutate, StoreApi } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { mergeDeepLeft } from 'ramda';

export type WidgetID = string;

export interface Message {
  type: string;
  data?: Record<string, unknown>[];
  origin?: WidgetID;
}

interface Widget {
  messages: Message[];
  emitters: WidgetID[];
  listeners: WidgetID[];
  updatedAt?: number;
  createdAt?: number;
}

interface WidgetState {
  widgets: {
    [key: WidgetID]: Widget;
  };
}

interface WidgetAction {
  registerWidget: (id: WidgetID) => void;
  publish: (
    id: WidgetID,
    messages: Message[],
    listenersPrefix?: string
  ) => void;
  broadcast: (messages: Message[]) => void;
  removeMessage: (id: WidgetID) => void;
  connect: (emitterId: WidgetID, listenerId: WidgetID) => void;
  removeConnection: (emitterId: WidgetID, listenerId: WidgetID) => void;
}

type StoreWithPersist = Mutate<
  StoreApi<WidgetAction & WidgetState>,
  [['zustand/persist', WidgetAction & WidgetState], ['zustand/devtools', never]]
>;

const withStorageDOMEvents = (store: StoreWithPersist) => {
  const storageEventCallback = (e: StorageEvent) => {
    if (e.key === store.persist.getOptions().name && e.newValue) {
      store.persist.rehydrate();
    }
  };

  window.addEventListener('storage', storageEventCallback);

  return () => {
    window.removeEventListener('storage', storageEventCallback);
  };
};

export const useWidgetStore = create<WidgetState & WidgetAction>()(
  devtools(
    persist(
      (set) => ({
        widgets: {},
        registerWidget: (id: WidgetID) =>
          set((state: WidgetState & WidgetAction) => {
            // Register widget only if it doesn't exist in the store yet
            if (state.widgets[id]) {
              // Update last accessed timestamp for existing widget
              return {
                widgets: {
                  ...state.widgets,
                  [id]: {
                    ...state.widgets[id],
                    updatedAt: Date.now(),
                  },
                },
              };
            }

            const now = Date.now();
            return {
              widgets: {
                ...state.widgets,
                [id]: {
                  messages: [],
                  emitters: [],
                  listeners: [],
                  updatedAt: now,
                  createdAt: now,
                },
              },
            };
          }),
        publish: (
          id: WidgetID,
          messages: Message[],
          listenersPrefix?: string
        ) =>
          set((state: WidgetState & WidgetAction) => {
            if (messages.length === 0) {
              return state;
            }

            let listeners: WidgetID[] = [];

            if (state.widgets[id]?.listeners) {
              listeners = [...state.widgets[id].listeners];
            }

            if (listenersPrefix) {
              const prefixListeners = Object.keys(state.widgets).filter(
                (widgetId) =>
                  widgetId.startsWith(listenersPrefix!) && widgetId !== id
              );
              listeners = [...listeners, ...prefixListeners];
            }

            if (listeners.length === 0) {
              return state;
            }

            const messagesWithOrigin = messages.map((message) => ({
              ...message,
              origin: id,
            }));

            const updatedWidgets = listeners.reduce((acc, listenerId) => {
              if (state.widgets[listenerId]) {
                acc[listenerId] = {
                  ...state.widgets[listenerId],
                  messages: messagesWithOrigin,
                  updatedAt: Date.now(),
                };
              }
              return acc;
            }, {} as { [key: WidgetID]: Widget });

            return {
              widgets: {
                ...state.widgets,
                ...updatedWidgets,
              },
            };
          }),
        removeMessage: (id: WidgetID) =>
          set((state: WidgetState & WidgetAction) => {
            const widget = {
              ...state.widgets[id],
              messages: [],
              updatedAt: Date.now(),
            };

            return {
              widgets: { ...state.widgets, [id]: widget },
            };
          }),
        broadcast: (messages: Message[]) =>
          set((state: WidgetState & WidgetAction) => {
            if (messages.length === 0) return state;
            const now = Date.now();
            const updatedWidgets = Object.keys(state.widgets).reduce(
              (acc, id) => {
                acc[id] = { ...state.widgets[id], messages, updatedAt: now };
                return acc;
              },
              {} as { [key: WidgetID]: Widget }
            );
            return { widgets: { ...state.widgets, ...updatedWidgets } };
          }),
        connect: (emitterId: WidgetID, listenerId: WidgetID) =>
          set((state: WidgetState & WidgetAction) => {
            // Connection is already established, change nothing
            if (state.widgets[listenerId].emitters.includes(emitterId)) {
              return state;
            }

            const now = Date.now();
            const listenerWidget = {
              ...state.widgets[listenerId],
              emitters: [
                ...(state.widgets[listenerId]?.emitters || []),
                emitterId,
              ],
              listeners: [
                ...(state.widgets[listenerId]?.listeners || []),
                emitterId,
              ],
              updatedAt: now,
            };

            const emitterWidget = {
              ...state.widgets[emitterId],
              listeners: [
                ...(state.widgets[emitterId]?.listeners || []),
                listenerId,
              ],
              emitters: [
                ...(state.widgets[emitterId]?.emitters || []),
                listenerId,
              ],
              updatedAt: now,
            };

            return {
              widgets: {
                ...state.widgets,
                [listenerId]: { ...listenerWidget },
                [emitterId]: { ...emitterWidget },
              },
            };
          }),
        removeConnection: (emitterId: WidgetID, listenerId: WidgetID) =>
          set((state: WidgetState & WidgetAction) => {
            // Connection is not found, change nothing
            if (!state.widgets[listenerId].emitters.includes(emitterId)) {
              return state;
            }

            const now = Date.now();
            const listenerWidget = {
              ...state.widgets[listenerId],
              messages: [],
              emitters: state.widgets[listenerId].emitters.filter(
                (emitter: WidgetID) => emitter !== emitterId
              ),
              updatedAt: now,
            };

            const emitterWidget = {
              ...state.widgets[emitterId],
              listeners: state.widgets[emitterId].listeners.filter(
                (listener: WidgetID) => listener !== listenerId
              ),
              updatedAt: now,
            };

            return {
              widgets: {
                ...state.widgets,
                [listenerId]: { ...listenerWidget },
                [emitterId]: { ...emitterWidget },
              },
            };
          }),
      }),
      {
        name: 'widget-store',
        merge: (persistedState: any, currentState: any) => {
          return mergeDeepLeft(persistedState, currentState);
        },
      }
    )
  )
);

withStorageDOMEvents(useWidgetStore);
